/**
 * The dated roadmap — an honest Gantt (D193 phase 2B) — from the roadmap
 * rows and the runs that served them, with no model:
 *
 *   what happened — each item's real work: from the first run that served it
 *                   to the last one's end (to now while one is going), read
 *                   from the runs (shared/schemas/projectRuns.ts) whose task
 *                   or plan names the item;
 *   what is ahead — a person's target where the row has one (the roadmap's
 *                   optional Target column), else a **forecast**, drawn
 *                   hatched and labelled as one.
 *
 * The forecast is kept simple enough to say in a sentence. When most items
 * carry an estimate (points, from the plans that serve them), the pace is the
 * estimated work finished in the last 12 weeks — "about 8 points a week" —
 * and each open item takes its own estimated share of it; an item with no
 * estimate counts as the average of those that have one. When most do not,
 * the pace is how many items were finished (each counts as one), so "about
 * one every N days". Either way the open items take that pace in turn — what
 * they wait on first, then in-progress before not-started, then the
 * roadmap's own order — and an item never ends before what it waits on, plus
 * its own share. Fewer than two items finished in the window is no pace, so
 * no forecast: those items are listed under "No date yet" and the picture
 * says why. Deterministic: the same rows, runs and day give the same dates.
 *
 * Pure: no Node, no DOM. MUST NOT import `vscode`.
 *
 * Console: copied from mju-ai-workbench shared/diagrams/ground/roadmapGantt.ts. The forecast is the
 * workbench's word for word; the grounding's spec, repair pass and lane rules come from ./ganttSpec.ts
 * (the Diagrammer's own, cut to the Gantt), and the spec's meta carries only its title and summary.
 */

import { roadmapDependencies, roadmapTargetEnd, roadmapTargetWords } from "@aioe/shared/roadmap/model";
import { comparePhase, DIAGRAM_TONES, laneOf, repairSpec, statusOf, type DiagramNote, type GroundRoadmapOptions, type RepairResult, type RoadmapRowLike, type TimelineDiagram, type TimelineItem, type TimelineLane } from "./ganttSpec.ts";

export type { RoadmapRowLike };

const DAY = 86_400_000;

/** A run as far as the Gantt needs it: when, and which roadmap items it served. */
export interface RoadmapRunLike {
  startedAt: string;
  endedAt?: string;
  roadmap?: ReadonlyArray<{ rootId: string; id: string }>;
}

/** What real work served one item: its first start and last end, as ISO days. */
export interface RoadmapActual {
  start: string;
  end: string;
  /** A run for it is still going: the end is today. */
  running: boolean;
  runs: number;
}

/** The pace the forecast goes by. */
export interface RoadmapPace {
  /** What the pace counts: estimated points (most items have an estimate), or items. */
  basis: "points" | "items";
  /** Items finished in the window. */
  finished: number;
  windowDays: number;
  /** About one item every this many days (the items basis; an average on the points basis). */
  daysPerItem: number;
  /** The points basis: the estimated work finished in the window, and how much a week that is. */
  points?: number;
  pointsPerWeek?: number;
  /** The window's first day (ISO). */
  since: string;
}

/** An estimate the forecast can use: a positive number of points. */
const estimateOf = (row: RoadmapRowLike): number | undefined => (typeof row.estimate === "number" && Number.isFinite(row.estimate) && row.estimate > 0 ? row.estimate : undefined);

/** "8 points a week", "1 point a week", "1.5 points a week": a pace that reads as a pace. */
export const pointsAWeek = (n: number): string => {
  const words = n >= 2 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
  return `${words} point${words === "1" ? "" : "s"} a week`;
};

/**
 * Whether the pace goes by estimates: more than half of the items finished in
 * the window have one, and more than half of the open ones — otherwise the
 * estimates say too little and the forecast counts items.
 */
export function paceByPoints(finished: readonly RoadmapRowLike[], open: readonly RoadmapRowLike[]): boolean {
  const most = (rows: readonly RoadmapRowLike[]) => rows.filter((r) => estimateOf(r) !== undefined).length * 2 > rows.length;
  return finished.length > 0 && most(finished) && (open.length === 0 || most(open));
}

export interface RoadmapForecast {
  pace?: RoadmapPace;
  /** By id: the forecast of an open item with no target (ISO days). */
  items: Map<string, { start: string; end: string }>;
  /** By id: an item with a target the pace says will be missed, and the day the pace says instead. */
  late: Map<string, string>;
  /** Open items that wait on each other in a loop: never forecast. */
  looped: string[];
  /** How it was made, or why there is none, in a sentence or two. */
  explanation: string;
}

const iso = (day: number): string => new Date(day * DAY).toISOString().slice(0, 10);
const dayOf = (text: string | undefined): number | undefined => {
  if (!text) return undefined;
  const t = Date.parse(text.length === 10 ? `${text}T00:00:00Z` : text);
  return Number.isFinite(t) ? Math.floor(t / DAY) : undefined;
};

/** "12 weeks" for 84 days, "30 days" otherwise. */
function windowWords(days: number): string {
  return days % 7 === 0 ? `${days / 7} weeks` : `${days} days`;
}

/**
 * What real work served each roadmap item, from the runs. At the repository
 * level a run serves the repository's own rows (refs with a repository id);
 * at the project level, the project's own items (`rootId: ""`).
 */
export function roadmapActuals(runs: readonly RoadmapRunLike[], opts: { level?: "repository" | "project"; now: string }): Map<string, RoadmapActual> {
  const level = opts.level ?? "repository";
  const now = Date.parse(opts.now.length === 10 ? `${opts.now}T23:59:59Z` : opts.now);
  const spans = new Map<string, { from: number; to: number; running: boolean; runs: number }>();
  for (const run of runs) {
    const from = Date.parse(run.startedAt);
    if (!Number.isFinite(from)) continue;
    const ended = run.endedAt ? Date.parse(run.endedAt) : Number.NaN;
    const running = !Number.isFinite(ended);
    const to = running ? Math.max(from, now) : Math.max(from, ended);
    for (const ref of run.roadmap ?? []) {
      if (level === "project" ? ref.rootId !== "" : ref.rootId === "") continue;
      const s = spans.get(ref.id);
      if (!s) spans.set(ref.id, { from, to, running, runs: 1 });
      else spans.set(ref.id, { from: Math.min(s.from, from), to: Math.max(s.to, to), running: s.running || running, runs: s.runs + 1 });
    }
  }
  const out = new Map<string, RoadmapActual>();
  for (const [id, s] of [...spans.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    out.set(id, { start: iso(Math.floor(s.from / DAY)), end: iso(Math.floor(s.to / DAY)), running: s.running, runs: s.runs });
  }
  return out;
}

/**
 * The open items in the order the forecast takes them: what they wait on
 * first, then in progress before not started, then the roadmap's order.
 * Items in a dependency loop are returned apart: they can never be finished.
 */
export function forecastOrder(rows: readonly RoadmapRowLike[]): { order: string[]; looped: string[] } {
  const open = rows.filter((r) => r.status === "in-progress" || r.status === "not-started");
  const openIds = new Set(open.map((r) => r.id));
  const rank = new Map(open.map((r, i) => [r.id, (r.status === "in-progress" ? 0 : 1) * 1_000_000 + i]));
  const waits = new Map(open.map((r) => [r.id, new Set((r.dependsOn ?? []).filter((d) => openIds.has(d) && d !== r.id))]));
  const order: string[] = [];
  const done = new Set<string>();
  for (;;) {
    const ready = open.filter((r) => !done.has(r.id) && [...waits.get(r.id)!].every((d) => done.has(d)));
    if (!ready.length) break;
    ready.sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
    const next = ready[0];
    order.push(next.id);
    done.add(next.id);
  }
  return { order, looped: open.filter((r) => !done.has(r.id)).map((r) => r.id) };
}

/**
 * The forecast for the open items: a person's target is kept (and said to be
 * late when the pace cannot meet it); an item with no target gets the pace's
 * date. No pace (fewer than two items finished in the window), no forecast.
 */
export function forecastRoadmap(rows: readonly RoadmapRowLike[], actuals: ReadonlyMap<string, RoadmapActual>, opts: { today: string; windowDays?: number }): RoadmapForecast {
  const today = dayOf(opts.today) ?? 0;
  const windowDays = Math.max(7, Math.round(opts.windowDays ?? 84));
  const since = today - windowDays;
  const finishedRows = rows.filter((r) => {
    if (r.status !== "done") return false;
    const end = dayOf(actuals.get(r.id)?.end);
    return end !== undefined && end > since && end <= today;
  });
  const finished = finishedRows.length;
  const { order, looped } = forecastOrder(rows);
  const items = new Map<string, { start: string; end: string }>();
  const late = new Map<string, string>();
  const loopWords = looped.length ? ` ${looped.join(", ")} wait${looped.length === 1 ? "s" : ""} on each other in a loop, so ${looped.length === 1 ? "it has" : "they have"} no forecast.` : "";
  if (finished < 2) {
    const why = finished === 0 ? `nothing on the roadmap was finished in the last ${windowWords(windowDays)}` : `only one item was finished in the last ${windowWords(windowDays)}`;
    return { items, late, looped, explanation: `No forecast: ${why}, so there is no pace to go by. Items without a target are listed under "No date yet".${loopWords}` };
  }
  const daysPerItem = Math.max(1, Math.round(windowDays / finished));
  const byId = new Map(rows.map((r) => [r.id, r]));
  // By points when most items are estimated: each item takes its own share of the work finished per day.
  const openRows = order.map((id) => byId.get(id)!);
  const byPoints = paceByPoints(finishedRows, openRows);
  const known = [...finishedRows, ...openRows].map(estimateOf).filter((n): n is number => n !== undefined);
  const average = known.length ? known.reduce((a, b) => a + b, 0) / known.length : 1;
  const sizeOf = (row: RoadmapRowLike) => estimateOf(row) ?? average;
  const points = byPoints ? finishedRows.reduce((sum, r) => sum + sizeOf(r), 0) : 0;
  const daysPerPoint = byPoints ? windowDays / points : 0;
  const daysFor = (row: RoadmapRowLike) => (byPoints ? Math.max(1, Math.round(sizeOf(row) * daysPerPoint)) : daysPerItem);
  const pointsPerWeek = (points * 7) / windowDays;
  const pace: RoadmapPace = byPoints
    ? { basis: "points", finished, windowDays, daysPerItem, points: Math.round(points), pointsPerWeek, since: iso(since) }
    : { basis: "items", finished, windowDays, daysPerItem, since: iso(since) };
  /** When each item is expected to be finished, for what waits on it. */
  const finish = new Map<string, number>();
  let clock = today;
  for (const id of order) {
    const row = byId.get(id)!;
    const after = Math.max(-Infinity, ...(row.dependsOn ?? []).map((d) => finish.get(d) ?? -Infinity));
    const days = daysFor(row);
    const end = Math.max(clock, after) + days;
    clock += days;
    const targetEnd = row.target ? dayOf(roadmapTargetEnd(row.target)) : undefined;
    if (targetEnd !== undefined) {
      if (end > targetEnd) late.set(id, iso(end));
      // What waits on it waits on whichever comes later: the person's date, or the pace's.
      finish.set(id, Math.max(end, targetEnd));
      continue;
    }
    finish.set(id, end);
    const started = row.status === "in-progress" ? dayOf(actuals.get(id)?.start) : undefined;
    const start = started ?? Math.max(today, end - days);
    items.set(id, { start: iso(Math.min(start, end)), end: iso(end) });
  }
  const lateWords = late.size ? ` At that pace ${[...late.entries()].map(([id, day]) => `${id} misses its target (${roadmapTargetWords(byId.get(id)!.target!)}; more like ${roadmapTargetWords(day.slice(0, 7))})`).join(", ")}.` : "";
  const paceWords = byPoints
    ? `${Math.round(points)} points of estimated work finished (${finished} items), about ${pointsAWeek(pointsPerWeek)}. Open items each take their estimated share of that pace in turn`
    : `${finished} items finished, about one every ${daysPerItem} day${daysPerItem === 1 ? "" : "s"}. Open items take that pace in turn`;
  const explanation = `Forecast from the last ${windowWords(windowDays)}: ${paceWords} — what they wait on first, then what is under way, then the roadmap's order — and an item with a target keeps it. A forecast is not a promise.${lateWords}${loopWords}`;
  return { pace, items, late, looped, explanation };
}

export interface GroundRoadmapGanttOptions extends Omit<GroundRoadmapOptions, "laneBy" | "recentDone" | "maxPerCell"> {
  /** Today (ISO day): the Today line, the forecast's start and the window's end. */
  today: string;
  /** The runs that served the items (their roadmap links say which). */
  runs?: readonly RoadmapRunLike[];
  /** Whose items the runs' links count for: this repository's rows, or the project's own. */
  level?: "repository" | "project";
  /** Lanes by id series (default), top-level area, or phase. */
  laneBy?: "series" | "area" | "phase";
  /** How far back the pace and the finished bars look (default 84 days: 12 weeks). */
  windowDays?: number;
  /** Most items drawn (default 60): open work first, then the most recently finished. */
  maxItems?: number;
}

export interface GanttGrounding extends RepairResult<TimelineDiagram> {
  forecast: RoadmapForecast;
  actuals: Map<string, RoadmapActual>;
  /** Ids drawn without a date ("No date yet"). */
  undated: string[];
}

function phaseLane(row: RoadmapRowLike): { id: string; label: string } {
  const p = row.phase && row.phase !== "-" ? row.phase.trim() : "";
  return p ? { id: `phase-${p.replace(/[^A-Za-z0-9._-]+/g, "") || "x"}`, label: /^\d+$/.test(p) ? `Phase ${p}` : p.length <= 3 ? `Phase ${p}` : p } : { id: "phase-none", label: "No phase" };
}

/**
 * The dated roadmap: finished work as solid bars from its runs, work under
 * way from its first run to its target or forecast, what is ahead at its
 * target (a diamond) or forecast (hatched), what has no date listed, the
 * critical path and what waits on what.
 */
export function groundRoadmapGantt(rows: readonly RoadmapRowLike[], opts: GroundRoadmapGanttOptions): GanttGrounding {
  const laneBy = opts.laneBy ?? "series";
  const today = dayOf(opts.today) ?? 0;
  const windowDays = Math.max(7, Math.round(opts.windowDays ?? 84));
  const actuals = roadmapActuals(opts.runs ?? [], { level: opts.level, now: opts.today });
  const forecast = forecastRoadmap(rows, actuals, { today: opts.today, windowDays });
  const maxItems = Math.max(5, opts.maxItems ?? 60);
  const since = today - windowDays;
  const notes: string[] = [];

  // Finished items drawn: those whose work ended in the window.
  const recentDone = rows.filter((r) => {
    if (r.status !== "done") return false;
    const end = dayOf(actuals.get(r.id)?.end);
    return end !== undefined && end > since;
  });
  const olderDone = rows.filter((r) => r.status === "done").length - recentDone.length;
  if (olderDone > 0) notes.push(`${olderDone} finished item${olderDone === 1 ? " is" : "s are"} not drawn: ${olderDone === 1 ? "its" : "their"} work was before ${iso(since)} or not recorded as runs.`);
  const deferred = rows.filter((r) => r.status === "deferred").length;
  if (deferred) notes.push(`${deferred} deferred item${deferred === 1 ? " is" : "s are"} left out.`);
  const open = rows.filter((r) => r.status === "in-progress" || r.status === "not-started");
  // Open work first, then the most recently finished, up to the cap.
  const doneByEnd = [...recentDone].sort((a, b) => (dayOf(actuals.get(b.id)!.end)! - dayOf(actuals.get(a.id)!.end)!) || a.id.localeCompare(b.id));
  const shownIds = new Set([...open, ...doneByEnd].slice(0, maxItems).map((r) => r.id));
  const left = open.length + recentDone.length - shownIds.size;
  if (left > 0) notes.push(`${left} more item${left === 1 ? "" : "s"} did not fit (${maxItems} at most): the oldest finished first.`);

  const lanes: TimelineLane[] = [];
  const items: TimelineItem[] = [];
  const undated: string[] = [];
  // Rows in the table's order, so lanes appear as the roadmap has them (phases sorted).
  const shown = rows.filter((r) => shownIds.has(r.id));
  const ordered = laneBy === "phase" ? [...shown].sort((a, b) => comparePhase(a.phase === "-" ? "~" : a.phase, b.phase === "-" ? "~" : b.phase)) : shown;
  for (const row of ordered) {
    const lane = laneBy === "phase" ? phaseLane(row) : laneOf(row, laneBy);
    if (!lanes.some((l) => l.id === lane.id)) lanes.push({ ...lane, tone: DIAGRAM_TONES[lanes.length % DIAGRAM_TONES.length] });
    const status = statusOf(row.status);
    const actual = actuals.get(row.id);
    const base: TimelineItem = {
      id: row.id,
      title: row.title.replace(/`/g, ""),
      lane: lane.id,
      ...(row.phase && row.phase !== "-" ? { phase: row.phase } : {}),
      status,
      ...(row.dependsOn?.length ? { dependsOn: [...row.dependsOn] } : {}),
      ...(row.target ? { target: row.target } : {}),
      ref: row.id,
    };
    const fc = forecast.items.get(row.id);
    if (status === "done") {
      items.push({ ...base, start: actual!.start, end: actual!.end });
    } else if (row.target) {
      // A person's date: a bar from the work's start when it has started, else a diamond at the target.
      const end = roadmapTargetEnd(row.target)!;
      const slip = forecast.late.get(row.id);
      const withSlip = slip ? { ...base, forecastEnd: slip } : base;
      items.push(actual ? { ...withSlip, start: actual.start, end: end < actual.start ? actual.start : end } : withSlip);
    } else if (fc) {
      items.push({ ...base, start: fc.start, end: fc.end, forecast: true });
    } else if (actual) {
      // Under way with no target and no pace: what has happened so far, to today.
      items.push({ ...base, start: actual.start, end: iso(Math.max(today, dayOf(actual.end) ?? today)) });
    } else {
      items.push(base);
      undated.push(row.id);
    }
  }

  const deps = roadmapDependencies(rows.map((r) => ({ id: r.id, status: statusOf(r.status), dependsOn: r.dependsOn ? [...r.dependsOn] : undefined })));
  const criticalPath = deps.criticalPath.filter((id) => shownIds.has(id));
  const counts = { done: recentDone.length, now: rows.filter((r) => r.status === "in-progress").length, next: rows.filter((r) => r.status === "not-started").length, targets: open.filter((r) => r.target).length };
  const summary = `${counts.done} finished in the last ${windowWords(windowDays)}, ${counts.now} under way and ${counts.next} still to start; ${counts.targets} with a target${forecast.items.size ? `, ${forecast.items.size} forecast` : ""}${criticalPath.length > 1 ? `; the critical path runs ${criticalPath.join(" → ")}` : ""}.`;
  const meta = { title: opts.title ?? "Roadmap: what happened and what is ahead", summary };
  const spec: TimelineDiagram = { kind: "timeline", meta, notes: forecast.explanation, scale: "date", lanes, items, now: iso(today), ...(criticalPath.length > 1 ? { criticalPath } : {}) };
  const repaired = repairSpec(spec);
  const extra: DiagramNote[] = [{ rule: "forecast", severity: "info", message: forecast.explanation }, ...notes.map((message): DiagramNote => ({ rule: "gantt", severity: "info", message }))];
  return { spec: repaired.spec, notes: [...repaired.notes, ...extra], forecast, actuals, undated };
}
